import { useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, UncontrolledTooltip } from 'reactstrap'
import s from './CloneOpportunityModal.scss'
import { AiOutlineExclamationCircle, AiFillCheckCircle } from 'react-icons/ai'
import {
  KeyValuePairs,
  Input,
  Checkbox,
  DatePicker as IdealDatePicker
} from 'components/ideal-core-ui'
import { useImmer } from 'use-immer'
import DatePicker from 'react-datepicker'
import '!style-loader!css-loader!react-datepicker/dist/react-datepicker.css' // eslint-disable-line
import moment from 'moment-timezone'
import { utcDate, timezoneDate } from 'utils/helpers'
import { getDateFormat } from 'utils/authService'
import Select from 'react-select'
import { fromClonedDeal } from './serializers'
import { makePostRequest } from 'utils/api'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useLoading } from 'hooks'
import { FaInfoCircle } from 'react-icons/fa'

const CloneOpportunityModal = ({
  toggleCloneOpportunityModal,
  showCloneOpportunityModal,
  dealName,
  dealManager,
  dueDate,
  followUpDate,
  users,
  orderedStatuses
}) => {
  const [state, setState] = useImmer({
    name: `${dealName} (copy)`,
    status: { value: 'NEW', label: 'New' },
    dueDate,
    followUpDate,
    dealManager,
    copyProposalTemplates: true,
    linkDeals: false,
    related: false
  })
  const dispatch = useDispatch()
  const { id } = useParams()
  const [, withLoadingLocks] = useLoading()
  const [clonedDealId, setClonedDealId] = useState('')

  const updateState = (value, key) => {
    setState(draft => {
      draft[key] = value
    })
  }

  const cloneOpportunity = async () => {
    try {
      const deal = await withLoadingLocks(
        makePostRequest(`/v1/deals/clone/${id}`, fromClonedDeal(state))
      )
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Opportunity cloned successfuly',
          level: 'success'
        }
      })
      setClonedDealId(deal.id)
    } catch (error) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'There was a problem cloning the opportunity',
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <Modal
        keyboard={false}
        backdrop="static"
        isOpen={showCloneOpportunityModal}
        toggle={toggleCloneOpportunityModal}
        size="md"
      >
        <ModalBody>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <p>
              {clonedDealId ? (
                <AiFillCheckCircle className={s.successIcon} />
              ) : (
                <AiOutlineExclamationCircle className={s.icon} />
              )}
            </p>
            <h4>
              {clonedDealId
                ? 'This opportunity was cloned successfully'
                : 'You are about to clone this opportunity'}
            </h4>
            <p>
              {clonedDealId
                ? 'Would you like to navigate to the new opportunity?'
                : 'All information will be copied to the new opportunity except for the settings below.'}
            </p>
          </div>
          {!clonedDealId && (
            <>
              <KeyValuePairs
                pairs={[
                  {
                    key: '* Opportunity Name',
                    value: (
                      <Input
                        type="text"
                        text={state.name || ''}
                        className={s.nameInput}
                        placeholder="Enter Opportunity Name"
                        onChangeCb={() => updateState(event.target.value, 'name')}
                      />
                    )
                  },
                  {
                    key: '* Opportunity Status',
                    value: (
                      <Select
                        value={state.status}
                        onChange={value => updateState(value, 'status')}
                        options={orderedStatuses}
                      />
                    )
                  },
                  {
                    key: '* Bid Due Date',
                    valueClass: [s.dueDateWrapper],
                    value: (
                      <DatePicker
                        selected={moment(
                          timezoneDate(state.dueDate, 'HH:mm:ss'),
                          `${getDateFormat()} HH:mm:ss`
                        ).toDate()}
                        startDate={moment(
                          timezoneDate(state.dueDate, 'HH:mm:ss'),
                          `${getDateFormat()} HH:mm:ss`
                        ).toDate()}
                        onChange={value => {
                          if (!value) return
                          updateState(utcDate(value), 'dueDate')
                        }}
                        showTimeSelect
                        timeFormat="h:mm aa"
                        timeIntervals={15}
                        dateFormat={`${getDateFormat()
                          .replace('YYYY', 'yyyy')
                          .replace('DD', 'dd')} h:mm aa`}
                        timeCaption="time"
                        placeholderText="Select deal due date"
                        className={s.dueDate}
                      />
                    )
                  },
                  {
                    key: '* Follow-up date',
                    value: (
                      <IdealDatePicker
                        value={state.followUpDate && timezoneDate(state.followUpDate)}
                        onChange={value => {
                          const date = value && value !== null ? utcDate(value) : ''
                          updateState(date, 'followUpDate')
                        }}
                      />
                    )
                  },
                  {
                    key: '* Opportunity Manager',
                    value: (
                      <Select
                        onChange={value => updateState(value, 'dealManager')}
                        options={users}
                        value={state.dealManager}
                      />
                    )
                  }
                ]}
              />
              <br />
              <Checkbox
                size="md"
                isChecked={state.copyProposalTemplates}
                triggerCheckbox={value => {
                  updateState(value.target.checked, 'copyProposalTemplates')
                }}
              />
              <p className={s.checkboxLabel}>Copy proposal templates to new opportunity.</p>
              <br />
              <Checkbox
                size="md"
                isChecked={state.linkDeals}
                triggerCheckbox={value => {
                  updateState(value.target.checked, 'linkDeals')
                }}
              />
              <p className={s.checkboxLabel}>Link these two opportunities.</p>
              {state.linkDeals && (
                <>
                  <Select
                    className={s.relInput}
                    options={[
                      { value: 'child', label: 'Child' },
                      { value: 'related', label: 'Related' }
                    ]}
                    onChange={value => {
                      updateState(value.value === 'related', 'related')
                    }}
                  />
                  <FaInfoCircle id="Type1" />
                  <UncontrolledTooltip placement="top" target="Type1">
                    Select <b>Child</b> if you want the new cloned opportunity to become a{' '}
                    <i>Child</i> of the existing opportunity. i.e. Metropolis Building is the{' '}
                    <i>Parent</i> original opportunity and the newly cloned opportunity Metropolis -
                    2nd Floor is the <i>Child</i> of Metropolis Building.
                    <br />
                    <br />
                    Select <b>Related</b> if you want the new cloned opportunity to become a{' '}
                    <i>sister</i> of the existing opportunity. i.e. Metropolis - 2nd Floor is the
                    original opportunity and the newly cloned opportunity Metropolis - 3nd Floor is
                    the <i>Sister</i> of Metropolis - 2nd Floor.
                  </UncontrolledTooltip>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleCloneOpportunityModal}>
            {clonedDealId ? 'Stay on page' : 'Cancel'}
          </Button>
          <Button
            className={s.saveBtn}
            onClick={
              clonedDealId
                ? () => {
                    // TODO: use router location after v6 gets it fixed
                    window.location.href = `/opportunities/${clonedDealId}`
                    toggleCloneOpportunityModal()
                  }
                : cloneOpportunity
            }
          >
            {clonedDealId ? 'Navigate to new opportunity' : 'Create New Opportunity'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CloneOpportunityModal
